import {
  Card,
  Typography,
  Divider,
  Row,
  Col,
  Space,
  Select,
  Tag,
  InputNumber,
} from "antd";

import { PercentageOutlined, CalculatorOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const PricingSidebar = ({
  pricing = {},
  selectedOffer,
  setSelectedOffer,
  offers = [],
  quotationSettings = {},
  setQuotationSettings,
  remainingTime = "",
  isExpired = false,
  mode = "edit",
}) => {
  const isViewMode = mode === "view";

  /* =========================================
     SAFE VALUES
  ========================================= */

const hotelTotal = Number(
  pricing?.hotelTotal ??
  pricing?.hotels ??
  0
);

const transportTotal = Number(
  pricing?.transportTotal ??
  pricing?.transport ??
  0
);

const activitiesTotal = Number(
  pricing?.activitiesTotal ??
  pricing?.activities ??
  0
);

const inclusionsTotal = Number(
  pricing?.inclusionsTotal ??
  pricing?.specialInclusions ??
  pricing?.inclusions ??
  0
);

const subtotal = Number(
  pricing?.subtotal ??
  pricing?.basePrice ??
  0
);

const markupAmount = Number(
  pricing?.markupAmount ??
  pricing?.margin ??
  pricing?.markup ??
  0
);

const discount = Number(
  pricing?.discount ??
  0
);

const gstAmount = Number(
  pricing?.gstAmount ??
  pricing?.taxes ??
  0
);

const grandTotal = Number(
  pricing?.grandTotal ??
  pricing?.total ??
  0
);

const originalPrice = Number(
  subtotal +
  markupAmount +
  gstAmount
);


const formattedRemainingTime = (() => {
  const totalSeconds =
    Number(remainingTime);

  if (
    !Number.isFinite(totalSeconds) ||
    totalSeconds <= 0
  ) {
    return "00:00:00";
  }

  const hours =
    Math.floor(
      totalSeconds / 3600
    );

  const minutes =
    Math.floor(
      (totalSeconds % 3600) / 60
    );

  const seconds =
    totalSeconds % 60;

  return [
    String(hours).padStart(2, "0"),
    String(minutes).padStart(2, "0"),
    String(seconds).padStart(2, "0"),
  ].join(":");
})();

  return (
    <Card
      style={{
        borderRadius: 20,
        width: "100%",
        border: "1px solid #e2e8f0",
        boxShadow: "0 10px 25px -5px rgba(15,23,42,0.05)",
        marginTop: 16,
        background: "#ffffff",
      }}
      bodyStyle={{ padding: "24px" }}
    >
      <div style={{ marginBottom: 16 }}>
        <Title
          level={4}
          style={{
            margin: 0,
            fontWeight: 700,
            color: "#0f172a",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <CalculatorOutlined style={{ color: "#059669" }} /> Pricing & Costing Summary
        </Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Real-time itemized cost breakdown, markup, GST and grand total summary
        </Text>
      </div>

      <Divider style={{ margin: "12px 0 20px 0" }} />

      <Row gutter={[24, 24]}>
        {/* ================= COLUMN 1: ITEMIZED BASE COSTING ================= */}
        <Col xs={24} md={8}>
          <div
            style={{
              background: "#f8fafc",
              padding: 16,
              borderRadius: 16,
              border: "1px solid #f1f5f9",
              height: "100%",
            }}
          >
            <Text
              strong
              style={{
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "#64748b",
                display: "block",
                marginBottom: 12,
              }}
            >
              Itemized Net Cost
            </Text>

            <Space direction="vertical" style={{ width: "100%" }} size={10}>
              <Row justify="space-between">
                <Text style={{ color: "#475569" }}>Hotels</Text>
                <Text strong style={{ color: "#0f172a" }}>
                  ₹ {hotelTotal.toLocaleString("en-IN")}
                </Text>
              </Row>

              <Row justify="space-between">
                <Text style={{ color: "#475569" }}>Transport</Text>
                <Text strong style={{ color: "#0f172a" }}>
                  ₹ {transportTotal.toLocaleString("en-IN")}
                </Text>
              </Row>

              <Row justify="space-between">
                <Text style={{ color: "#475569" }}>Activities</Text>
                <Text strong style={{ color: "#0f172a" }}>
                  ₹ {activitiesTotal.toLocaleString("en-IN")}
                </Text>
              </Row>

              <Row justify="space-between">
                <Text style={{ color: "#475569" }}>Special Inclusions</Text>
                <Text strong style={{ color: "#0f172a" }}>
                  ₹ {inclusionsTotal.toLocaleString("en-IN")}
                </Text>
              </Row>

              <Divider style={{ margin: "6px 0" }} />

              <Row justify="space-between">
                <Text strong style={{ color: "#0f172a", fontSize: 15 }}>
                  Net Base Cost
                </Text>
                <Text strong style={{ color: "#0284c7", fontSize: 16 }}>
                  ₹ {subtotal.toLocaleString("en-IN")}
                </Text>
              </Row>
            </Space>
          </div>
        </Col>

        {/* ================= COLUMN 2: MARKUP & TAX SETTINGS ================= */}
        <Col xs={24} md={8}>
          <div
            style={{
              background: "#f8fafc",
              padding: 16,
              borderRadius: 16,
              border: "1px solid #f1f5f9",
              height: "100%",
            }}
          >
            <Text
              strong
              style={{
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "#64748b",
                display: "block",
                marginBottom: 12,
              }}
            >
              Markup, Tax & Offers
            </Text>

            <Space direction="vertical" style={{ width: "100%" }} size={12}>
              {/* Offers Dropdown */}
              <div>
                <Space style={{ marginBottom: 4 }}>
                  <PercentageOutlined style={{ color: "#059669" }} />
                  <Text strong style={{ fontSize: 13, color: "#334155" }}>
                    Select Offer / Promo
                  </Text>
                </Space>
                <Select
                  placeholder={offers?.length ? "Select Offer" : "No Active Offers"}
                  disabled={!offers?.length || isExpired || isViewMode}
                  style={{ width: "100%" }}
                  allowClear
                  value={selectedOffer?._id}
                  onChange={(value) => {
                    if (!value) {
                      setSelectedOffer(null);
                      return;
                    }
                    const selected = offers.find((offer) => offer._id === value);
                    setSelectedOffer(selected || null);
                  }}
                  options={(offers || []).map((offer) => ({
                    label: `${offer.title} (${offer.offerCode})`,
                    value: offer._id,
                  }))}
                />
                {selectedOffer && (
                  <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <Tag color={isExpired ? "red" : "green"}>
                      {selectedOffer?.title || selectedOffer?.offerCode || "Offer Applied"}
                    </Tag>
                    <Tag color={isExpired ? "red" : "blue"}>
                      {isExpired ? "EXPIRED" : `LOCK ${formattedRemainingTime}`}
                    </Tag>
                  </div>
                )}
              </div>

              {/* Inputs Row */}
              <Row gutter={12}>
                <Col span={8}>
                  <Text strong style={{ fontSize: 12, color: "#475569" }}>
                    Markup %
                  </Text>
                  <InputNumber
                    disabled={isExpired || isViewMode}
                    min={0}
                    max={500}
                    value={quotationSettings?.markup}
                    onChange={(value) =>
                      setQuotationSettings({
                        ...quotationSettings,
                        markup: value || 0,
                      })
                    }
                    style={{ width: "100%", marginTop: 4 }}
                  />
                </Col>

                <Col span={8}>
                  <Text strong style={{ fontSize: 12, color: "#475569" }}>
                    GST %
                  </Text>
                  <InputNumber
                    disabled={isExpired || isViewMode}
                    min={0}
                    max={28}
                    value={quotationSettings?.gst}
                    onChange={(value) =>
                      setQuotationSettings({
                        ...quotationSettings,
                        gst: value || 0,
                      })
                    }
                    style={{ width: "100%", marginTop: 4 }}
                  />
                </Col>

                <Col span={8}>
                  <Text strong style={{ fontSize: 12, color: "#475569" }}>
                    Discount (₹)
                  </Text>
                  <InputNumber
                    disabled={isExpired || isViewMode}
                    min={0}
                    value={quotationSettings?.manualDiscount}
                    onChange={(value) =>
                      setQuotationSettings({
                        ...quotationSettings,
                        manualDiscount: value || 0,
                      })
                    }
                    style={{ width: "100%", marginTop: 4 }}
                    prefix="₹"
                  />
                </Col>
              </Row>
            </Space>
          </div>
        </Col>

        {/* ================= COLUMN 3: FINAL GRAND TOTAL BANNER ================= */}
        <Col xs={24} md={8}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: "100%",
              gap: 12,
            }}
          >
            {/* Breakdown summary */}
            <div style={{ padding: "0 4px" }}>
              <Row justify="space-between" style={{ marginBottom: 4 }}>
                <Text style={{ color: "#64748b", fontSize: 13 }}>Markup Added</Text>
                <Text strong style={{ color: "#16a34a", fontSize: 13 }}>
                  + ₹ {markupAmount.toLocaleString("en-IN")}
                </Text>
              </Row>
              <Row justify="space-between" style={{ marginBottom: 4 }}>
                <Text style={{ color: "#64748b", fontSize: 13 }}>Discount Applied</Text>
                <Text strong style={{ color: "#dc2626", fontSize: 13 }}>
                  - ₹ {discount.toLocaleString("en-IN")}
                </Text>
              </Row>
              <Row justify="space-between" style={{ marginBottom: 4 }}>
                <Text style={{ color: "#64748b", fontSize: 13 }}>GST Tax Amount</Text>
                <Text strong style={{ color: "#475569", fontSize: 13 }}>
                  + ₹ {gstAmount.toLocaleString("en-IN")}
                </Text>
              </Row>
            </div>

            {/* Lock Status indicator */}
            <Card
              size="small"
              bordered={false}
              style={{
                borderRadius: 12,
                background: isExpired ? "#fef2f2" : "#eff6ff",
                border: isExpired ? "1px solid #fecaca" : "1px solid #bfdbfe",
              }}
            >
              <Space direction="vertical" size={2}>
                <Text strong style={{ color: isExpired ? "#b91c1c" : "#1d4ed8", fontSize: 12 }}>
                  {isExpired
                    ? "Offer Expired"
                    : selectedOffer
                    ? "Locked Proposal Pricing"
                    : "Draft Proposal Pricing"}
                </Text>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {isExpired
                    ? "Pricing protection expired. Reapply offer."
                    : selectedOffer
                    ? "Customer pricing protected until timer ends."
                    : "Quotation still editable."}
                </Text>
              </Space>
            </Card>

            {/* Dark Final Total Card */}
            <div
              style={{
                padding: "16px 20px",
                borderRadius: 16,
                background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                color: "white",
                boxShadow: "0 10px 20px -5px rgba(15,23,42,0.3)",
              }}
            >
              <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", fontWeight: 700 }}>
                {isViewMode ? "PROPOSAL PREVIEW" : "FINAL SELL PRICE"}
              </Text>

              <div style={{ display: "flex", justify: "space-between", alignItems: "center", marginTop: 6 }}>
                <div>
                  {discount > 0 && (
                    <div
                      style={{
                        textDecoration: "line-through",
                        color: "rgba(255,255,255,0.55)",
                        fontSize: 13,
                        marginBottom: 2,
                      }}
                    >
                      ₹ {(grandTotal + discount).toLocaleString("en-IN")}
                    </div>
                  )}
                  <Title
                    level={2}
                    style={{
                      margin: 0,
                      color: "white",
                      fontWeight: 800,
                      fontSize: 26,
                    }}
                  >
                    ₹ {grandTotal.toLocaleString("en-IN")}
                  </Title>
                </div>

                <Tag
                  color={isExpired ? "red" : "blue"}
                  style={{ borderRadius: 999, fontWeight: 600, padding: "4px 12px" }}
                >
                  {isExpired ? "EXPIRED" : selectedOffer ? "LOCKED PRICE" : "DRAFT"}
                </Tag>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Card>
  );
};

export default PricingSidebar;
